import React, { JSX, useCallback, useContext, useEffect, useState } from 'react';
import { Button, Col, Collapse, Container, Row } from 'reactstrap';
import DealFinderPaxSelector from './DealFinderPaxSelector';
import {
  ApiGeoUnitFromJSON,
  ApiGetPackagesRequest,
  ApiGetPackagesRequestFromJSON,
  ApiHotel,
  ApiItemType,
  ApiPackageModel,
  ApiPackageModelDefinition,
  ApiRoomContainer
} from '@ibe/api';
import dayjs from 'dayjs';
import minMax from 'dayjs/plugin/minMax';
import utc from 'dayjs/plugin/utc';
import isBetween from 'dayjs/plugin/isBetween';
import tz from 'dayjs/plugin/timezone';
import {
  LoadingOverlay,
  Marker,
  ServiceSearchDetailParams,
  ServiceSearchParams,
  useApi,
  useConfig
} from '@ibe/components';
import DealFinderDatePicker from './DealFinderDatePicker';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarWeek,
  faChevronDown,
  faHotel,
  faInfoCircle,
  faUser
} from '@fortawesome/free-solid-svg-icons';
import Keys from '../../Translations/generated/deal-finder.en.json.keys';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import CmsPackage from '../../Models/CMS/CmsPackage';
import DealFinderHotelPicker from './DealFinderHotelPicker';
import { observer } from 'mobx-react';
import { QueryUtils } from '../../Util/QueryUtils';
import useQuery from '../../Util/useQuery';
import { LoggerFactory } from '@ibe/services';
import CmsHotelData from '../../Models/CMS/CmsHotelData';
import useCmsHotel from '../../Hooks/useCmsHotel';
import { useMount } from 'react-use';
import ServiceSearchProps from '../SearchWidget/ServiceSearchProps';
import FacetType from '../../Models/FacetType';
import HotelDetailsPopup from '../HotelDetailsPopup/HotelDetailsPopup';
import IbeConfig from '../../Models/CMS/IbeConfig';
import { useExternalSearchParams } from '../../Config/ExternalParamsContext';
import getExternalConfig from '../../Config/externalConfigs';
import useCms from '../../Hooks/useCms';
import CartUtil from '../../Util/CartUtil';
import useCmsPackage from '../../Hooks/useCmsPackage';
import DealFinderPropsResolver from './DealFinderPropsResolver';
import { ActivePackageConfiguration, DealFinderProps } from './DealFinderProps';
import MegConfigModel from '../../Config/MegConfigModel';
import DealFinderContext from '../../Contexts/DealFinderContext';
import OffersUtil from '../../Util/OffersUtil';
import {
  DealFinderPaxConfiguration,
  OfferSettings
} from '../../Models/CMS/CmsDealFinderGlobalConfig';
import useDealFinderGlobalConfigService from '../../Hooks/useDealFinderGlobalConfigService';
import { getServiceCodeFromId } from '../../Util/utils';
import { configService } from '../../Config/config';
import { dealFinderOpenDatePickerEvent } from './DealFinderEvents';
import { getCustomMapConfigOld } from '../../Util/mapUtils';

dayjs.extend(minMax);
dayjs.extend(utc);
dayjs.extend(tz);
dayjs.extend(isBetween);

const INVALID_DATE_STRING = 'Invalid Date';

const DEFAULT_AVAILABILITY_TOLERANCE = 62;

export interface DealFinderPaxConfigurationResult {
  resolvedPaxConfiguration: DealFinderPaxConfiguration | null;
  isSuccessfullyResolved: boolean;
}

const logger = LoggerFactory.get('DealFinder');

const DealFinder = observer(
  (props: ServiceSearchProps & DealFinderProps & { maxCapacity?: number }): JSX.Element => {
    const {
      isInWidgetMode,
      dealFinderWidgetModeProps,
      isEnabled,
      searchParams,
      packageModel,
      alternativePackageModelFromProps,
      onSubmit,
      store,
      isMobileView,
      datePickerProps,
      activePackageConfigurationFromProps,
      onHotelSearch,
      packageCart,
      minAdults,
      maxAdults,
      maxChildren,
      maxChildAge,
      maxCapacity,
      maxRooms
    } = props;

    const config = useConfig() as MegConfigModel;
    const { t } = useTranslation('deal-finder');
    const query = useQuery();
    const dealFinderContext = useContext(DealFinderContext);

    const propsResolver = new DealFinderPropsResolver(isInWidgetMode, query, searchParams);

    const cms = useCms();
    const api = useApi();
    const cmsPackageService = useCmsPackage();
    const cmsHotelService = useCmsHotel();
    const cmsDealFinderConfigurationService = useDealFinderGlobalConfigService();

    const [cmsHotels, setCmsHotels] = useState<CmsHotelData[]>([]);
    const [cmsDealFinderPaxConfigurations, setCmsDealFinderPaxConfigurations] = useState<
      DealFinderPaxConfiguration[]
    >([]);
    const [cmsDealFinderOfferSettings, setCmsDealFinderOfferSettings] = useState<OfferSettings[]>(
      []
    );

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [preselectAllHotels, setPreselectAllHotels] = useState<boolean>(false);

    const itemFromQuery = QueryUtils.getItem(query);
    const salesChannelCodeFromQuery = QueryUtils.getSalesChannelCode(query);

    const getCurrentVisibleMonthState = (): string => {
      if (searchParams?.startDate) {
        return searchParams.startDate;
      }
      return sessionStorage.getItem('Current_Visible_Month') || dayjs().format('YYYY-MM-DD');
    };

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const [alternativePackageModel, setAlternativePackageModel] = useState<CmsPackage | undefined>(
      alternativePackageModelFromProps
    );
    const [activePackageConfiguration, setActivePackageConfiguration] = useState<
      ActivePackageConfiguration | undefined
    >({
      activePackageModel: packageModel,
      activePackageSalesChannelCode: salesChannelCodeFromQuery ?? ''
    });

    // ============================== search parameters ===============================================================
    const [paxSelection, setPaxSelection] = useState<ApiRoomContainer[]>(
      (searchParams?.occupancy as ApiRoomContainer[]) || ([] as ApiRoomContainer[])
    );

    const [dateRange, setDateRange] = useState<[string | undefined, string | undefined]>([
      searchParams?.startDate ? searchParams.startDate : undefined, //NOSONAR
      searchParams?.endDate ? searchParams.endDate : undefined //NOSONAR
    ]);
    const [currentVisibleMonth, setCurrentVisibleMonth] = useState<string>(
      getCurrentVisibleMonthState()
    );

    const [selectedHotels, setSelectedHotels] = useState<CmsHotelData[]>([]);

    // =================================================================================================================

    const [isMobileDatePickerOpen, setIsMobileDatePickerOpen] = useState<boolean>(
      propsResolver.getResolvedExpandDateSelection(packageModel, dealFinderWidgetModeProps)
    );
    const [isMobilePaxPickerOpen, setIsMobilePaxPickerOpen] = useState<boolean>(
      propsResolver.getResolvedExpandPaxSelection(packageModel, dealFinderWidgetModeProps)
    );
    const [isMobileHotelPickerOpen, setIsMobileHotelPickerOpen] = useState<boolean>(
      propsResolver.getResolvedExpandHotelSelection(packageModel, dealFinderWidgetModeProps)
    );
    const [isHotelDetailsOpen, setIsHotelDetailsOpen] = useState(false);

    const [dateSelectionError, setDateSelectionError] = useState<boolean>();
    const [dateRangeSelectionError, setDateRangeSelectionError] = useState<boolean>();
    const [paxSelectionError, setPaxSelectionError] = useState<boolean>();

    const [availabilityPackage, setAvailabilityPackage] = useState<ApiPackageModel | undefined>();

    const format: string =
      config.displayFormatDate[i18next.language ?? localStorage.getItem('i18nextLng') ?? ''] ??
      config.displayFormatDate[config.defaultLanguage];

    // ========================================= URL related variables =================================================
    const detailsHotel = store?.detail;

    const externalSearchParams = useExternalSearchParams();
    const translationsUrl = externalSearchParams
      ? getExternalConfig(externalSearchParams, {})?.translationsUrl
      : '';

    const customMapConfig = getCustomMapConfigOld(cms.ibeConfig, translationsUrl);

    useMount(() => {
      if (packageModel?.dealFinderConfig?.relatedHotels || isInWidgetMode) {
        setIsLoading(true);

        const relatedHotelsCodes: string[] = [];
        const preSelectedRelatedHotelsHotelsCodes: string[] = [];

        const orderMap = new Map();

        if (isInWidgetMode) {
          if (dealFinderWidgetModeProps?.assignedHotelCodes?.length) {
            relatedHotelsCodes.push(...dealFinderWidgetModeProps.assignedHotelCodes);
          }
          if (dealFinderWidgetModeProps?.preselectedHotelCodes?.length) {
            preSelectedRelatedHotelsHotelsCodes.push(
              ...dealFinderWidgetModeProps.preselectedHotelCodes
            );
          }
        } else if (itemFromQuery) {
          const selectedHotelCode = getServiceCodeFromId(itemFromQuery);

          if (selectedHotelCode) {
            relatedHotelsCodes.push(selectedHotelCode);
            preSelectedRelatedHotelsHotelsCodes.push(selectedHotelCode);
          }
        } else if (packageModel?.dealFinderConfig?.relatedHotels?.length) {
          for (const relatedHotel of packageModel?.dealFinderConfig?.relatedHotels) {
            orderMap.set(relatedHotel.serviceCode, relatedHotel.orderNumber);
            relatedHotelsCodes.push(relatedHotel.serviceCode);

            if (relatedHotel.preselectHotel) {
              preSelectedRelatedHotelsHotelsCodes.push(relatedHotel.serviceCode);
            }
          }
        }
        // eslint-disable-next-line
        // @ts-ignore
        Promise.all([
          Promise.resolve(cmsDealFinderConfigurationService.fetchPaxConfigurations()),
          Promise.resolve(cmsHotelService.fetchHotels(relatedHotelsCodes))
        ])
          .then(
            ([dealFinderGlobalConfig, cmsHotelDataItems]: [
              DealFinderPaxConfiguration[],
              CmsHotelData[]
            ]) => {
              const sortedHotels = [...cmsHotelDataItems].sort((a, b) => {
                return (
                  (orderMap.get(a.typeCode) ?? Number.MAX_VALUE) -
                  (orderMap.get(b.typeCode) ?? Number.MAX_VALUE)
                );
              });
              setCmsHotels(sortedHotels);

              const preSelectedRelatedHotelsHotels: CmsHotelData[] = [];
              for (const relatedHotel of cmsHotelDataItems) {
                if (preSelectedRelatedHotelsHotelsCodes.includes(relatedHotel.typeCode ?? '')) {
                  preSelectedRelatedHotelsHotels.push(relatedHotel);
                }

                setSelectedHotels(preSelectedRelatedHotelsHotels);
              }

              setCmsDealFinderPaxConfigurations(dealFinderGlobalConfig);
            }
          )
          .finally(() => {
            setIsLoading(false);
          });
      }

      dealFinderOpenDatePickerEvent.subscribe(() => setIsMobileDatePickerOpen(true));
    });

    useEffect(() => {
      setActivePackageConfiguration({
        activePackageModel: packageModel,
        activePackageSalesChannelCode: salesChannelCodeFromQuery ?? ''
      });

      if ((packageModel || isInWidgetMode) && !alternativePackageModel) {
        const alternativePackageCode =
          dealFinderWidgetModeProps?.alternativePackageCode ??
          packageModel?.dealFinderConfig?.alternativePackageCode;
        if (alternativePackageCode) {
          setIsLoading(true);
          cmsPackageService
            .fetchPackage(alternativePackageCode as string) //NOSONAR
            .then(returnedPackageModel => {
              if (returnedPackageModel) {
                setAlternativePackageModel(returnedPackageModel as CmsPackage); //NOSONAR
              }
            })
            .finally(() => setIsLoading(false));
        }
      }
    }, [packageModel]);

    useEffect(() => {
      updatePackageAvailabilityAndOffers();
    }, [
      selectedHotels,
      selectedHotels.length,
      currentVisibleMonth,
      activePackageConfigurationFromProps?.activePackageModel?.name,
      activePackageConfiguration?.activePackageModel?.name,
      paxSelection[0].childrenAges.length,
      paxSelection[0].adults
    ]);

    useEffect(() => {
      setDateRange([searchParams?.startDate, searchParams?.endDate]);
    }, [searchParams?.startDate, searchParams?.endDate]);

    function updatePackageAvailabilityAndOffers() {
      if (selectedHotels.length) {
        const availabilitySearchStartDate = dayjs(currentVisibleMonth)
          .startOf('month')
          .format('YYYY-MM-DD');

        setIsLoading(true);
        Promise.all([
          loadAvailabilities(availabilitySearchStartDate),
          loadOffers(availabilitySearchStartDate)
        ]).finally(() => setIsLoading(false));
      }
    }

    function loadAvailabilities(searchStartDate: string): Promise<void> {
      const defaultPaxConfiguration = getDefaultPaxConfiguration();
      if (defaultPaxConfiguration.resolvedPaxConfiguration) {
        const request = buildPackageAvailabilityRequest(
          searchStartDate,
          defaultPaxConfiguration.resolvedPaxConfiguration
        );
        return api
          .getPackageAvailability(request)
          .then(response => {
            setAvailabilityPackage(response.packages[0] ?? []);
          })
          .catch(error => {
            logger.log(error);
          });
      }
      return Promise.resolve();
    }

    function loadOffers(searchStartDate: string): Promise<void> {
      if (isInWidgetMode && !dealFinderWidgetModeProps?.displayOffers) {
        return Promise.resolve();
      }

      const searchEndDate = dayjs(searchStartDate).endOf('month').format('YYYY-MM-DD');

      return cmsDealFinderConfigurationService
        .fetchOffersData(searchStartDate, searchEndDate)
        .then(value => {
          setCmsDealFinderOfferSettings(value || []);
        });
    }

    function buildPackageAvailabilityRequest(
      searchStartDate: string,
      resolvedPaxConfiguration: DealFinderPaxConfiguration
    ): ApiGetPackagesRequest {
      const paxConfiguration: ApiRoomContainer[] = [
        {
          adults: resolvedPaxConfiguration.numberOfAdults
            ? Number(resolvedPaxConfiguration.numberOfAdults)
            : 0,
          children: resolvedPaxConfiguration.numberOfChildren
            ? Number(resolvedPaxConfiguration.numberOfChildren)
            : 0,
          infants: resolvedPaxConfiguration.numberOfInfants
            ? Number(resolvedPaxConfiguration.numberOfInfants)
            : 0,
          childrenAges:
            Number(resolvedPaxConfiguration.numberOfChildren) &&
            resolvedPaxConfiguration.defaultChildrenAges
              ? resolvedPaxConfiguration.defaultChildrenAges
                  .map(Number)
                  .filter(n => !Number.isNaN(n))
              : [],
          infantsAges:
            Number(resolvedPaxConfiguration.numberOfInfants) &&
            resolvedPaxConfiguration.defaultInfantsAges
              ? resolvedPaxConfiguration.defaultInfantsAges
                  .map(Number)
                  .filter(n => !Number.isNaN(n))
              : []
        }
      ];

      return ApiGetPackagesRequestFromJSON({
        destination: '',
        startDate: searchStartDate,
        classCode: '',
        calculatePrices: false,
        subType: ApiItemType.PACKAGE,
        salesChannel: propsResolver.getResolvedPackageSalesChannelCode(
          activePackageConfigurationFromProps,
          activePackageConfiguration
        ),
        mainServiceCodes: selectedHotels.map(hotel => hotel.typeCode),
        serviceUnitCodes: [],
        mainServiceTypeCodes: [],
        toleranceMinus: config.dealFinderDefaultToleranceMinus ?? DEFAULT_AVAILABILITY_TOLERANCE,
        tolerancePlus: config.dealFinderDefaultTolerancePlus ?? DEFAULT_AVAILABILITY_TOLERANCE,
        packageCode: propsResolver.getResolvedPackageCode(
          activePackageConfigurationFromProps,
          activePackageConfiguration
        ),
        roomContainer: paxConfiguration,
        facetType: FacetType.TRAVEL_START_DATE
      });
    }

    function validateProvidedAges(): boolean {
      let isValid = true;
      for (const room of paxSelection) {
        if (room.children !== room.childrenAges.length) {
          isValid = false;
        }
        const validNumbers = !room.childrenAges.some(Number.isNaN);
        if (!validNumbers) {
          isValid = validNumbers;
        }
      }

      return isValid;
    }

    const validateDateRange = (
      startDate: string | undefined,
      endDate: string | undefined
    ): boolean => {
      if (
        !startDate ||
        !endDate ||
        (!!startDate && startDate === INVALID_DATE_STRING) ||
        (!!endDate && endDate === INVALID_DATE_STRING)
      )
        return false;

      const dayjsStart = dayjs(startDate, config.formatDate);
      const dayjsEnd = dayjs(endDate, config.formatDate);

      if (!dayjsStart.isValid() || !dayjsEnd.isValid()) {
        return false;
      }
      if (dayjsStart.isAfter(dayjsEnd)) {
        return false;
      }
      if (dayjsStart.isSame(dayjsEnd) && datePickerProps.minDateRangeSpan !== 0) {
        return false;
      }
      const now = dayjs().startOf('day');
      return dayjsStart.isSame(now) || dayjsStart.isAfter(now);
    };

    async function findSingleDealFinderHotelId(): Promise<string | undefined> {
      let hotelListResponse;

      if (!QueryUtils.getItem(query) && selectedHotels.length == 1) {
        if (!store?.list.length) {
          store?.setSearchParams({
            ...searchParams,
            occupancy: [...paxSelection],
            startDate: dayjs(dateRange[0] as string).format(config.formatDate),
            endDate: dayjs(dateRange[1] as string).format(config.formatDate)
          } as Partial<ServiceSearchDetailParams>);

          setIsLoading(true);
          hotelListResponse = await store?.loadHotels(true);
          setIsLoading(false);
        }
        const hotelsList: ApiHotel[] = store?.list.length
          ? store?.list
          : hotelListResponse?.hotels ?? [];
        if (hotelsList?.length) {
          return hotelsList.find(listItem => listItem.code === selectedHotels[0].typeCode)?.id;
        }
      }

      return undefined;
    }

    function updateDealFinderContext(
      selectedHotelsList: CmsHotelData[],
      cmsHotelsList: CmsHotelData[]
    ): void {
      if (selectedHotelsList.length > 0 && selectedHotelsList.length < cmsHotelsList.length) {
        dealFinderContext.setSelectedHotels(selectedHotelsList);
      } else {
        dealFinderContext.setSelectedHotels([]);
      }
    }

    async function onSearch() {
      setDateRangeSelectionError(false);
      setDateSelectionError(false);
      setPaxSelectionError(false);

      // check paxes
      const isChildrenSelectionValid = validateProvidedAges();
      if (!isChildrenSelectionValid) {
        setPaxSelectionError(true);
        return;
      }

      //   check dates
      if (dateRange[0] && dateRange[1]) {
        const isDateRangeValid = validateDateRange(
          dayjs(dateRange[0]).format(config.formatDate),
          dayjs(dateRange[1]).format(config.formatDate)
        );

        if (!isDateRangeValid) {
          setDateRangeSelectionError(true);
          return;
        }
      } else {
        setDateSelectionError(true);
        return;
      }

      if (paxSelection && dateRange[0] && dateRange[1]) {
        const dealFinderHotelId = await findSingleDealFinderHotelId();
        if (onHotelSearch && store && dealFinderHotelId) {
          onHotelSearch(
            {
              ...searchParams,
              productId: dealFinderHotelId,
              occupancy: [...paxSelection],
              startDate: dayjs(dateRange[0]).format(config.formatDate),
              endDate: dayjs(dateRange[1]).format(config.formatDate)
            } as Partial<ServiceSearchDetailParams>,
            store?.listOptions,
            {
              code: propsResolver.getResolvedPackageCode(
                activePackageConfigurationFromProps,
                activePackageConfiguration
              ),
              salesChannelCode: propsResolver.getResolvedPackageSalesChannelCode(
                activePackageConfigurationFromProps,
                activePackageConfiguration
              )
            } as Partial<ServiceSearchParams>
          );
        } else if (onSubmit) {
          updateDealFinderContext(selectedHotels, cmsHotels);
          onSubmit(
            {
              ...searchParams,
              occupancy: [...paxSelection],
              startDate: dayjs(dateRange[0]).format(config.formatDate),
              endDate: dayjs(dateRange[1]).format(config.formatDate)
            } as ServiceSearchParams,
            {
              code: propsResolver.getResolvedPackageCode(
                activePackageConfigurationFromProps,
                activePackageConfiguration
              ),
              salesChannelCode: propsResolver.getResolvedPackageSalesChannelCode(
                activePackageConfigurationFromProps,
                activePackageConfiguration
              )
            } as Partial<ServiceSearchParams>
          );
        }
      }
    }

    const hasDateRange = (): boolean => {
      return (
        !!dateRange &&
        !!dateRange[0] &&
        dateRange[0] !== INVALID_DATE_STRING &&
        !!dateRange[1] &&
        dateRange[1] !== INVALID_DATE_STRING
      );
    };

    function handlePaxChange(pax: ApiRoomContainer[]): void {
      if (pax) {
        setPaxSelection([...pax]);
      }
    }

    function handleDateChange(startDate: string | undefined, endDate: string | undefined) {
      setDateRange([startDate, endDate]);
    }

    function handleHotelSelection(selectedHotel: CmsHotelData) {
      let resultingArray;

      if (selectedHotels.some(value => value.typeCode === selectedHotel.typeCode)) {
        resultingArray = selectedHotels.filter(value => value.typeCode !== selectedHotel.typeCode);
      } else {
        resultingArray = selectedHotels.concat(selectedHotel);
      }

      if (preselectAllHotels) {
        setPreselectAllHotels(false);
      }

      if (resultingArray.length == 0) {
        setAvailabilityPackage(undefined);
      }

      setSelectedHotels(resultingArray);

      setDateRange([undefined, undefined]);
    }

    function handleAlternativePackageSelection(
      newActivePackageModel: ApiPackageModel,
      newActiveSalesChannelCode: string
    ) {
      setActivePackageConfiguration({
        activePackageModel: newActivePackageModel,
        activePackageSalesChannelCode: newActiveSalesChannelCode
      });
    }

    const getNumberOfAdults = (): number => {
      return (
        paxSelection?.reduce((total: number, current: ApiRoomContainer) => {
          return total + (current.adults || 0);
        }, 0) || 0
      );
    };

    const getNumberOfChildren = (): number => {
      return (
        paxSelection?.reduce((total: number, current: ApiRoomContainer) => {
          return total + (current.children || 0) + (current.infants || 0);
        }, 0) || 0
      );
    };

    const hasSelectedPax = (): boolean => {
      return paxSelection.some(
        selection => !!selection.adults || !!selection.children || !!selection.infants
      );
    };

    function getHotelSectionLabel(): string {
      if (selectedHotels.length > 0) {
        if (selectedHotels.length == 1) {
          return selectedHotels[0].title
            ? selectedHotels[0].title
            : `1 ${t(Keys.selectedHotelsLabel)}`; //NOSONAR
        } else {
          return `${selectedHotels.length} ${t(Keys.selectedHotelsLabel)}`;
        }
      }

      return t(Keys.select);
    }

    function handleSelectAllChange() {
      const newValue = !preselectAllHotels;
      setPreselectAllHotels(newValue);

      if (newValue) {
        setSelectedHotels(cmsHotels);
      } else {
        setSelectedHotels([]);
      }
    }

    function getLabel(): string {
      if (hasSelectedPax()) {
        return `${paxSelection.length} ${t(Keys.room, { count: paxSelection.length })}${
          getNumberOfAdults() > 0
            ? `, ${getNumberOfAdults()} ${t(Keys.adult, {
                count: getNumberOfAdults()
              })}`
            : ''
        }${
          getNumberOfChildren() > 0
            ? `, ${getNumberOfChildren()} ${t(Keys.child, {
                count: getNumberOfChildren()
              })}`
            : ''
        }`;
      }
      return t(Keys.pleaseSelect);
    }

    function onMonthChange(date?: Date) {
      if (date) {
        sessionStorage.setItem('Current_Visible_Month', dayjs(date).format('YYYY-MM-DD'));
        setCurrentVisibleMonth(dayjs(date).format('YYYY-MM-DD'));
      }
    }

    function getDefaultPaxConfiguration(): DealFinderPaxConfigurationResult {
      if (!cmsDealFinderPaxConfigurations?.length) {
        return { isSuccessfullyResolved: false, resolvedPaxConfiguration: null };
      }

      let resolvedPaxConfiguration: DealFinderPaxConfiguration | null = null;

      for (const paxConfiguration of cmsDealFinderPaxConfigurations ?? []) {
        if (isDefaultPaxConfiguration(paxConfiguration)) {
          resolvedPaxConfiguration = paxConfiguration;
          break;
        }
      }

      if (resolvedPaxConfiguration) {
        return { isSuccessfullyResolved: true, resolvedPaxConfiguration: resolvedPaxConfiguration };
      } else {
        const mainPaxConfiguration = cmsDealFinderPaxConfigurations.find(
          value => value.mainPaxConfiguration
        );

        if (mainPaxConfiguration) {
          return { isSuccessfullyResolved: false, resolvedPaxConfiguration: mainPaxConfiguration };
        }
      }

      return { isSuccessfullyResolved: false, resolvedPaxConfiguration: null };
    }

    const isDefaultPaxConfiguration = useCallback(
      (testedPaxConfiguration: DealFinderPaxConfiguration) => {
        const infantMaxAge = configService.get().traveler.maxInfantAge;

        const numberOfChildren = paxSelection[0].childrenAges.filter(num => num > infantMaxAge)
          .length;
        const numberOfInfants = paxSelection[0].childrenAges.filter(num => num <= infantMaxAge)
          .length;

        return (
          paxSelection.length === 1 &&
          (paxSelection[0].adults ?? 0) == (testedPaxConfiguration.numberOfAdults ?? 0) &&
          (numberOfChildren ?? 0) == (testedPaxConfiguration.numberOfChildren ?? 0) &&
          (numberOfInfants ?? 0) == (testedPaxConfiguration.numberOfInfants ?? 0)
        );
      },
      [paxSelection]
    );

    const getOffersForSelectedHotels: () => OfferSettings[] = useCallback(() => {
      const result: OfferSettings[] = [];
      const packageCode = propsResolver.getResolvedPackageCode(
        activePackageConfigurationFromProps,
        activePackageConfiguration
      );
      for (const hotel of selectedHotels) {
        result.push(
          ...OffersUtil.getDealFinderRelatedOffers(
            cmsDealFinderOfferSettings ?? [],
            packageCode,
            hotel.typeCode
          )
        );
      }
      return [...new Map(result.map(offer => [offer.name, offer])).values()];
    }, [cmsDealFinderOfferSettings, currentVisibleMonth, selectedHotels.length]);

    function renderAlternativePackageOptions(): JSX.Element {
      if (packageModel && alternativePackageModel && !itemFromQuery) {
        return (
          <Row
            className={`${
              isMobileView ? 'justify-content-start pl-2' : 'justify-content-center'
            } align-items-end meg_deal-finder_alternative_packages_container`}
          >
            <Col
              className={`${
                isMobileView && !isActive(packageModel?.name) ? 'col-4' : 'col-auto'
              }  px-1`}
            >
              <button
                className={`meg_deal-finder_alternative_package ${
                  isActive(packageModel?.name)
                    ? 'meg_deal-finder_alternative_package__selected'
                    : 'meg_deal-finder_alternative_package__not-selected'
                }`}
                onClick={() => {
                  handleAlternativePackageSelection(
                    packageModel as ApiPackageModel,
                    salesChannelCodeFromQuery ?? ''
                  );
                }}
              >
                {packageModel.title}
              </button>
            </Col>
            <Col
              className={`${
                isMobileView && !isActive(alternativePackageModel?.name) ? 'col-4' : 'col-auto'
              }  px-1`}
            >
              <button
                className={`meg_deal-finder_alternative_package ${
                  isActive(alternativePackageModel?.name)
                    ? 'meg_deal-finder_alternative_package__selected'
                    : 'meg_deal-finder_alternative_package__not-selected'
                }`}
                onClick={() => {
                  handleAlternativePackageSelection(
                    alternativePackageModel as ApiPackageModel,
                    packageModel?.dealFinderConfig?.alternativePackageSalesChannelCode ?? ''
                  );
                }}
              >
                {alternativePackageModel.title}
              </button>
            </Col>
          </Row>
        );
      }

      return <></>;
    }

    const getLowPriceThreshold = (): number | undefined => {
      return (
        dealFinderWidgetModeProps?.lowPriceThreshold ??
        packageModel?.dealFinderConfig?.lowPriceThreshold
      );
    };

    const getMediumPriceThreshold = () => {
      return (
        dealFinderWidgetModeProps?.mediumPriceThreshold ??
        packageModel?.dealFinderConfig?.mediumPriceThreshold
      );
    };

    function isActive(code: string | undefined): boolean {
      return code == activePackageConfiguration?.activePackageModel?.name;
    }

    function renderDesktopVersion(): JSX.Element {
      return (
        <>
          <Col
            className="pl-0 verticalDivider deal-finder_main-column"
            xs={12}
            md={dealFinderWidgetModeProps?.hideHotelSelection ? 6 : 4}
          >
            <DealFinderPaxSelector
              value={[...paxSelection]}
              initialOccupancy={[...paxSelection]}
              onChange={handlePaxChange}
              isPaxSelectionNotValid={paxSelectionError}
              validateChildrenSelection={() => true}
              maxCapacity={maxCapacity ?? config.paxRoomConfig.maxCapacity}
              maxAdults={maxAdults}
              maxChildAge={maxChildAge}
              maxChildren={maxChildren}
              minAdults={minAdults}
              maxRooms={maxRooms}
              isMobile={isMobileView}
            />
          </Col>
          {dealFinderWidgetModeProps?.hideHotelSelection ? (
            <></>
          ) : (
            <Col className="verticalDivider deal-finder_main-column" xs={12} md={4}>
              <DealFinderHotelPicker
                packageModel={packageModel}
                selectedHotels={selectedHotels}
                hotels={cmsHotels}
                onHotelSelect={handleHotelSelection}
                isMobile={isMobileView}
                selectAllHotels={preselectAllHotels}
                onSelectAll={() => {
                  handleSelectAllChange();
                }}
              />
            </Col>
          )}
          <Col
            className="pr-0 deal-finder_main-column"
            xs={12}
            md={dealFinderWidgetModeProps?.hideHotelSelection ? 6 : 4}
            id="desktop_deal_finder_date_picker"
          >
            <DealFinderDatePicker
              packageModel={packageModel}
              startDate={dateRange[0]}
              endDate={dateRange[1]}
              onChange={(startDate, endDate) => {
                handleDateChange(startDate, endDate);
              }}
              isDateNotValid={dateSelectionError}
              isDateRangeNotValid={dateRangeSelectionError}
              searchParams={searchParams}
              minDateRangeSpan={datePickerProps.minDateRangeSpan}
              maxDateRangeSpan={datePickerProps.maxDateRangeSpan}
              seasons={datePickerProps.seasons}
              highlightDatesInformation={datePickerProps.highlightDatesInformation}
              availabilityPackage={selectedHotels.length ? availabilityPackage : undefined}
              onMonthChange={onMonthChange}
              dealFinderPaxConfigurationResult={getDefaultPaxConfiguration()}
              lowPriceThreshold={getLowPriceThreshold()}
              mediumPriceThreshold={getMediumPriceThreshold()}
              calendarRelevantOffers={getOffersForSelectedHotels()}
              currentlyVisibleMonth={currentVisibleMonth}
            />
          </Col>
          <Col xs={12} className="pt-2">
            <Button className="w-100 search-button p-2" color="primary" onClick={onSearch}>
              {t(Keys.search)}
            </Button>
          </Col>
        </>
      );
    }

    function renderMobileVersion(): JSX.Element {
      return (
        <Col>
          <Row>
            <Col>
              <Row className="py-2">
                <Col className="align-content-center" xs={10}>
                  <FontAwesomeIcon className="mr-2 primaryIcon" icon={faUser} />
                  {getLabel()}
                </Col>
                <Col className="align-content-center text-center" xs={2}>
                  <Button
                    className="deal-finder__chevron-button"
                    color="link"
                    onClick={() => setIsMobilePaxPickerOpen(!isMobilePaxPickerOpen)}
                  >
                    <FontAwesomeIcon
                      className={`deal-finder__chevron ${
                        isMobilePaxPickerOpen ? 'deal-finder__chevronUp' : ''
                      }`}
                      icon={faChevronDown}
                    />
                  </Button>
                </Col>
              </Row>
              <Row className="py-1 horizontalDivider">
                <Col>
                  <Collapse isOpen={isMobilePaxPickerOpen}>
                    <DealFinderPaxSelector
                      value={[...paxSelection]}
                      initialOccupancy={[...paxSelection]}
                      onChange={handlePaxChange}
                      isPaxSelectionNotValid={paxSelectionError}
                      validateChildrenSelection={() => true}
                      maxCapacity={maxCapacity ?? config.paxRoomConfig.maxCapacity}
                      maxAdults={maxAdults}
                      maxChildAge={maxChildAge}
                      maxChildren={maxChildren}
                      minAdults={minAdults}
                      maxRooms={maxRooms}
                      isMobile={isMobileView}
                    />
                  </Collapse>
                </Col>
              </Row>
              {dealFinderWidgetModeProps?.hideHotelSelection ? (
                <></>
              ) : (
                <>
                  <Row className="py-2">
                    <Col className="align-content-center" xs={10}>
                      <Row>
                        <Col xs={8}>
                          <FontAwesomeIcon className="mr-2 primaryIcon" icon={faHotel} />
                          {getHotelSectionLabel()}
                          {detailsHotel ? (
                            <button
                              className="p-0 border-0 bg-transparent iso__accommodation-bar__hotel-section__name d-inline"
                              onClick={() => setIsHotelDetailsOpen(true)}
                            >
                              <FontAwesomeIcon className={'info'} icon={faInfoCircle} />
                            </button>
                          ) : (
                            <> </>
                          )}
                        </Col>
                      </Row>
                    </Col>
                    <Col className="align-content-center text-center" xs={2}>
                      <Button
                        className="deal-finder__chevron-button "
                        color="link"
                        onClick={() => setIsMobileHotelPickerOpen(!isMobileHotelPickerOpen)}
                      >
                        <FontAwesomeIcon
                          className={`deal-finder__chevron ${
                            isMobileHotelPickerOpen ? 'deal-finder__chevronUp' : ''
                          }`}
                          icon={faChevronDown}
                        />
                      </Button>
                    </Col>
                  </Row>
                  <Row className="py-1 horizontalDivider">
                    <Col>
                      <Collapse isOpen={isMobileHotelPickerOpen}>
                        <Container>
                          <DealFinderHotelPicker
                            packageModel={packageModel}
                            hotels={cmsHotels}
                            onHotelSelect={handleHotelSelection}
                            selectedHotels={selectedHotels}
                            isMobile={isMobileView}
                            selectAllHotels={preselectAllHotels}
                            onSelectAll={() => {
                              handleSelectAllChange();
                            }}
                          />
                        </Container>
                      </Collapse>
                    </Col>
                  </Row>
                </>
              )}

              <Row className="py-1 ">
                <Col className="align-content-center" xs={10}>
                  <FontAwesomeIcon className="mr-2 primaryIcon" icon={faCalendarWeek} />
                  {hasDateRange()
                    ? `${dayjs(dateRange[0] as string).format(format)} - ${dayjs(
                        dateRange[1] as string
                      ).format(format)}`
                    : t(Keys.pleaseSelect)}
                </Col>
                <Col className="align-content-center text-center" xs={2}>
                  <Button
                    className="deal-finder__chevron-button"
                    color="link"
                    onClick={() => setIsMobileDatePickerOpen(!isMobileDatePickerOpen)}
                  >
                    <FontAwesomeIcon
                      className={`deal-finder__chevron ${
                        isMobileDatePickerOpen ? 'deal-finder__chevronUp' : ''
                      }`}
                      icon={faChevronDown}
                    />
                  </Button>
                </Col>
              </Row>
              <Row className="py-1">
                <Col id="mobile_deal_finder_date_picker">
                  <Collapse isOpen={isMobileDatePickerOpen}>
                    <DealFinderDatePicker
                      packageModel={packageModel}
                      startDate={dateRange[0]}
                      endDate={dateRange[1]}
                      onChange={(startDate, endDate) => {
                        handleDateChange(startDate, endDate);
                      }}
                      isDateNotValid={dateSelectionError}
                      isDateRangeNotValid={dateRangeSelectionError}
                      searchParams={searchParams}
                      minDateRangeSpan={datePickerProps.minDateRangeSpan}
                      maxDateRangeSpan={datePickerProps.maxDateRangeSpan}
                      seasons={datePickerProps.seasons}
                      highlightDatesInformation={datePickerProps.highlightDatesInformation}
                      availabilityPackage={selectedHotels.length ? availabilityPackage : undefined}
                      onMonthChange={onMonthChange}
                      dealFinderPaxConfigurationResult={getDefaultPaxConfiguration()}
                      lowPriceThreshold={getLowPriceThreshold()}
                      mediumPriceThreshold={getMediumPriceThreshold()}
                      calendarRelevantOffers={getOffersForSelectedHotels()}
                      currentlyVisibleMonth={currentVisibleMonth}
                    />
                  </Collapse>
                </Col>
              </Row>
            </Col>
          </Row>
          <Row className="pt-2">
            <Col>
              <Button className="w-100 search-button p-2" color="primary" onClick={onSearch}>
                {t(Keys.search)}
              </Button>
            </Col>
          </Row>
          {detailsHotel && isHotelDetailsOpen ? (
            <HotelDetailsPopup
              hotelModel={detailsHotel}
              onClose={() => setIsHotelDetailsOpen(false)}
              useLazyLoading
              mapConfig={customMapConfig}
              searchParams={searchParams}
              packageModel={packageModel as ApiPackageModelDefinition}
              readPriceFromRoomRates
              readFromCheapestRoom={true}
              characteristics={
                packageCart ? CartUtil.getCharacteristicsFromComponents(packageCart) : undefined
              }
            />
          ) : (
            <> </>
          )}
        </Col>
      );
    }

    function render(): JSX.Element {
      if (isMobileView) {
        return renderMobileVersion();
      } else {
        return renderDesktopVersion();
      }
    }

    return isEnabled ? (
      <LoadingOverlay isLoading={isLoading} className="w-100">
        <Row>
          <Col>
            {isMobileView ? renderAlternativePackageOptions() : <> </>}
            <Row
              className={`p-2 meg_deal-finder_container ${
                isMobileView ? '' : 'meg_deal-finder_container-desktop'
              } justify-content-center`}
            >
              {render()}
            </Row>
          </Col>
        </Row>
      </LoadingOverlay>
    ) : (
      <></>
    );
  }
);

export default DealFinder;
